import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Checkbox,
  TextField,
  Paper,
  Tabs,
  Tab,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { useTheme } from '@mui/material/styles';

// Clean single-file GraphQcViewer: left controls, center image+SVG, right tabs
export default function GraphQcViewer({ graph }) {
  const imgRef = useRef(null);
  const resizeRef = useRef(null);
  const svgRef = useRef(null);
  const rightScrollRef = useRef(null);
  const farRightScrollRef = useRef(null);
  const isSyncingRef = useRef(false);
  const draggedSvgYRef = useRef(null); // Track the Y coordinate being dragged
  const [draggedSvgY, setDraggedSvgY] = useState(null); // State version to trigger re-renders
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
  const [lines, setLines] = useState([]);
  const [sharedTabIndex, setSharedTabIndex] = useState(0);
  const [plotWidth, setPlotWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [isEraseMode, setIsEraseMode] = useState(false);
  const [nullValue, setNullValue] = useState('-999.25');
  const [draggedPointIndex, setDraggedPointIndex] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const buttonPanelRef = useRef(null);
  const buttonPanelTopRef = useRef(null);
  const rightTabsRef = useRef(null);
  const rightTabsTopRef = useRef(null);
  const farRightTabsRef = useRef(null);
  const farRightTabsTopRef = useRef(null);
  const [rightTabsSticky, setRightTabsSticky] = useState(false);
  const [farRightTabsSticky, setFarRightTabsSticky] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (graph && Array.isArray(graph.lines)) {
      setLines(graph.lines.map((l) => ({ ...l, visible: l.visible !== false })));
      setSharedTabIndex(0);
    } else {
      setLines([]);
    }
  }, [graph]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setDimensions((prev) => ({ ...prev, width: entry.contentRect.width, height: entry.contentRect.height }));
    });
    observer.observe(img);
    return () => observer.disconnect();
  }, []);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (img) setDimensions((prev) => ({ ...prev, naturalWidth: img.naturalWidth, naturalHeight: img.naturalHeight }));
  };

  useEffect(() => {
    const { naturalWidth, naturalHeight } = dimensions;
    if (!naturalWidth || !naturalHeight) return;
    lines.forEach((line) => (line.points || []).forEach(([x, y]) => {
      if (typeof x !== 'number' || typeof y !== 'number') return;
      if (x < 0 || y < 0 || x > naturalWidth || y > naturalHeight) {
        console.warn(`GraphQcViewer: point (${x},${y}) exceeds image dimensions (${naturalWidth}x${naturalHeight}).`);
      }
    }));
  }, [dimensions.naturalWidth, dimensions.naturalHeight, lines]);

  const pointsToString = (pts) => (pts || []).map(([x, y]) => `${x},${y}`).join(' ');

  // Filter points to exclude null values and create connected segments
  const getFilteredPolylines = (coords) => {
    if (!coords || coords.length === 0) return [];
    
    const segments = [];
    let currentSegment = [];
    
    coords.forEach((pt) => {
      const y = Array.isArray(pt) ? pt[1] : (pt && pt.y);
      // Check if point is a null value (comparing as string or number)
      if (y === nullValue || y === parseFloat(nullValue)) {
        // End current segment if it exists
        if (currentSegment.length > 0) {
          segments.push(currentSegment);
          currentSegment = [];
        }
      } else {
        // Add point to current segment
        currentSegment.push(pt);
      }
    });
    
    // Add any remaining segment
    if (currentSegment.length > 0) {
      segments.push(currentSegment);
    }
    
    return segments;
  };

  const toggleLine = (id) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, visible: !line.visible } : line)));
  const changeColor = (id, color) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, color } : line)));
  const changeName = (id, name) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, name } : line)));
  const changeUnit = (id, unit) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, unit } : line)));
  const changeAxisMin = (id, axisMin) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, axis_min: axisMin } : line)));
  const changeAxisMax = (id, axisMax) => setLines((prev) => prev.map((line) => (line.id === id ? { ...line, axis_max: axisMax } : line)));

  // Synchronize table scrolling
  const syncRightScroll = (scrollTop) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (farRightScrollRef.current && farRightScrollRef.current.scrollTop !== scrollTop) {
      farRightScrollRef.current.scrollTop = scrollTop;
    }
    isSyncingRef.current = false;
  };

  const syncFarRightScroll = (scrollTop) => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (rightScrollRef.current && rightScrollRef.current.scrollTop !== scrollTop) {
      rightScrollRef.current.scrollTop = scrollTop;
    }
    isSyncingRef.current = false;
  };

  useEffect(() => {
    // Store the initial top position of the button panel and tables
    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (buttonPanelRef.current && buttonPanelTopRef.current === null) {
        buttonPanelTopRef.current = buttonPanelRef.current.getBoundingClientRect().top + window.scrollY;
      }
      if (rightTabsRef.current && rightTabsTopRef.current === null) {
        rightTabsTopRef.current = rightTabsRef.current.getBoundingClientRect().top + window.scrollY;
      }
      if (farRightTabsRef.current && farRightTabsTopRef.current === null) {
        farRightTabsTopRef.current = farRightTabsRef.current.getBoundingClientRect().top + window.scrollY;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const navbarHeight = 64;
      
      // Button panel sticky
      if (buttonPanelRef.current && buttonPanelTopRef.current !== null) {
        if (scrollPosition + navbarHeight >= buttonPanelTopRef.current) {
          setIsSticky(true);
        } else {
          setIsSticky(false);
        }
      }
      
      // Right tabs sticky
      if (rightTabsRef.current && rightTabsTopRef.current !== null) {
        if (scrollPosition + navbarHeight >= rightTabsTopRef.current) {
          setRightTabsSticky(true);
        } else {
          setRightTabsSticky(false);
        }
      }
      
      // Far right tabs sticky
      if (farRightTabsRef.current && farRightTabsTopRef.current !== null) {
        if (scrollPosition + navbarHeight >= farRightTabsTopRef.current) {
          setFarRightTabsSticky(true);
        } else {
          setFarRightTabsSticky(false);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = e.clientX - resizeRef.current.getBoundingClientRect().left;
      setPlotWidth(Math.max(100, newWidth));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const [dragStartY, setDragStartY] = useState(null);
  const [dragStartX, setDragStartX] = useState(null);

  const handleSvgMouseMove = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return;

    // Get point in SVG coordinate space
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    const svgX = svgPt.x;
    const svgY = svgPt.y;

    // Store the Y coordinate for table scrolling
    if ((isEditingMode || isEraseMode) && draggedPointIndex !== null) {
      draggedSvgYRef.current = svgY;
      setDraggedSvgY(svgY);
    }

    // Handle erase mode
    if (isEraseMode && draggedPointIndex !== null) {
      setLines((prevLines) =>
        prevLines.map((line, idx) => {
          if (idx !== sharedTabIndex || !line.img_coords) return line;
          
          const updatedCoords = line.img_coords.map(([px, py]) => {
            if (Math.abs(py - svgY) < 1.5) {
              return [nullValue, py];
            }
            return [px, py];
          });
          
          let updatedPlotPoints = line.plot_points;
          if (Array.isArray(line.plot_points) && line.plot_points.length === line.img_coords.length) {
            updatedPlotPoints = line.plot_points.map((plotPt, ptIdx) => {
              if (Math.abs(line.img_coords[ptIdx][1] - svgY) < 1.5) {
                return [nullValue, plotPt[1]];
              }
              return plotPt;
            });
          }
          
          return { ...line, img_coords: updatedCoords, plot_points: updatedPlotPoints };
        })
      );
      return;
    }

    // Handle edit mode (existing logic)
    setLines((prevLines) =>
      prevLines.map((line, idx) => {
        if (idx !== sharedTabIndex || !line.img_coords) return line;
        const updatedCoords = line.img_coords.map(([px, py]) => {
          // Update x for all points within 5px of current mouse y position
          if (Math.abs(py - svgY) < 1.5) {
            return [svgX, py];
          }
          return [px, py];
        });
        
        // Also update corresponding plot_points if they exist and have the same length
        let updatedPlotPoints = line.plot_points;
        if (Array.isArray(line.plot_points) && line.plot_points.length === line.img_coords.length) {
          updatedPlotPoints = line.plot_points.map((plotPt, ptIdx) => {
            // Update x for points at the same index if the img_coords point was updated
            if (Math.abs(line.img_coords[ptIdx][1] - svgY) < 1.5) {
              return [svgX, plotPt[1]];
            }
            return plotPt;
          });
        }
        
        return { ...line, img_coords: updatedCoords, plot_points: updatedPlotPoints };
      })
    );
  }, [dimensions.naturalWidth, sharedTabIndex, isEditingMode, isEraseMode, draggedPointIndex, nullValue]);

  const handleSvgMouseUp = useCallback(() => {
    setDraggedPointIndex(null);
    setDragStartY(null);
    setDragStartX(null);
    setDraggedSvgY(null);
  }, []);

  const handleSvgMouseDown = (e) => {
    if (!isEditingMode && !isEraseMode) return;
    const svg = svgRef.current;
    if (!svg) return;

    // Get point in SVG coordinate space
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgPt = pt.matrixTransform(svg.getScreenCTM().inverse());
    const svgX = svgPt.x;
    const svgY = svgPt.y;

    const currentCurve = lines[sharedTabIndex];
    if (!currentCurve || !currentCurve.img_coords) return;

    // Find closest point or start drag
    let closestIndex = -1;
    let closestDistance = 10;
    currentCurve.img_coords.forEach((pt, idx) => {
      const distance = Math.sqrt(Math.pow(pt[0] - svgX, 2) + Math.pow(pt[1] - svgY, 2));
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = idx;
      }
    });

    // Set drag active (even if no close point found, we can still drag)
    setDraggedPointIndex(closestIndex >= 0 ? closestIndex : 0);
  };

  useEffect(() => {
    if (draggedPointIndex !== null && (isEditingMode || isEraseMode)) {
      document.addEventListener('mousemove', handleSvgMouseMove);
      document.addEventListener('mouseup', handleSvgMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleSvgMouseMove);
        document.removeEventListener('mouseup', handleSvgMouseUp);
      };
    }
  }, [draggedPointIndex, isEditingMode, isEraseMode, handleSvgMouseMove, handleSvgMouseUp]);

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, mt: 2, width: '100%', alignItems: 'flex-start' }}>
      <Paper sx={{ p: 2, width: { xs: '100%', sm: 220 }, flexShrink: 0, bgcolor: 'transparent', color: 'inherit' }}>
        <Box sx={{ width: { xs: '100%', sm: 420 }, flexShrink: 0, alignSelf: 'flex-start' }}>
          <Paper sx={{ p: 1, height: '370px', overflow: 'hidden', bgcolor: '#fff', color: 'text.primary', boxSizing: 'border-box' }} elevation={1}>
            <LeftTabs lines={lines} changeName={changeName} toggleLine={toggleLine} changeColor={changeColor} changeUnit={changeUnit} changeAxisMin={changeAxisMin} changeAxisMax={changeAxisMax} containerHeight={dimensions.height} tabIndex={sharedTabIndex} onTabChange={setSharedTabIndex} />
          </Paper>
          <Typography variant="body2" sx={{ mt: 3, color: '#fff' }}>Null Value</Typography>
          <TextField 
            size="small" 
            fullWidth
            value={nullValue}
            onChange={(e) => setNullValue(e.target.value)}
            sx={{ bgcolor: '#fff', borderRadius: 1 }}
          />
          <Typography sx={{ mt: 2, color: '#fff', bgcolor: '#333', p: 1, borderRadius: 1, textAlign: 'left' }}>
            Change View:
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;-Click and drag image with no buttons<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;toggled on to move image<br />            
            &nbsp;&nbsp;&nbsp;&nbsp;-Use mouse wheel to scroll in & out<br /><br />
            Edit Curve:
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;-Toggle "Edit Curve Data" button then<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;click and drag to adjust values<br />
            &nbsp;&nbsp;&nbsp;&nbsp;-Toggle "Erase Points" then<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;click and drag to set selected<br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;points to null values<br />
          </Typography>
        </Box>
      </Paper>

      <Box ref={resizeRef} sx={{ position: 'relative', minWidth: '350px', width: `${plotWidth}px`, flexShrink: 0, ml: { xs: '100%', sm: '225px' }, mr: { xs: '100%', sm: '100px' } }}>
        <Box sx={{ maxWidth: '100%' }}>
          <TransformWrapper
            initialScale={1}
            minScale={0.5}
            maxScale={4}
            limitToBounds={false}
            centerOnInit={false}
            wheel={{ disabled: isEditingMode || isEraseMode, step: 0.1 }}
            panning={{ disabled: isEditingMode || isEraseMode, velocityDisabled: true }}
            pinch={{ disabled: isEditingMode || isEraseMode }}
            doubleClick={{ disabled: isEditingMode || isEraseMode }}
            alignmentAnimation={{ disabled: true }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <Paper 
                  ref={buttonPanelRef}
                  sx={{ 
                    p: 1, 
                    mb: 1, 
                    bgcolor: '#fff', 
                    display: 'flex', 
                    gap: 1, 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    position: isSticky ? 'sticky' : 'relative',
                    top: isSticky ? '70px' : 'auto',
                    zIndex: isSticky ? 1000 : 1
                  }} 
                  elevation={isSticky ? 3 : 1}
                >
                  <Button variant="contained" size="small" onClick={() => resetTransform()} sx={{backgroundColor: '#000000ff', '&:hover': { backgroundColor: '#5a5a5aff' }}}>
                    Reset View
                  </Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => setIsEditingMode(!isEditingMode)}
                    sx={{ 
                      backgroundColor: isEditingMode ? '#3ba1a8ff' : '#000000ff',
                      '&:hover': { backgroundColor: isEditingMode ? '#3ba1a8ff' : '#5a5a5aff' }
                    }}
                  >
                    {isEditingMode ? 'Editing ON' : 'Edit Curve Data'}
                  </Button>
                  <Button 
                    variant="contained" 
                    size="small" 
                    onClick={() => setIsEraseMode(!isEraseMode)}
                    sx={{
                      backgroundColor: isEraseMode ? '#3ba1a8ff' : '#000000ff',
                      '&:hover': { backgroundColor: isEraseMode ? '#3ba1a8ff' : '#5a5a5aff' }
                    }}
                  >
                    {isEraseMode ? 'Erasing ON' : 'Erase Points'}
                  </Button>
                </Paper>
                <Box sx={{ p: 1, mb: 1, bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Selected Curve: {lines.length > 0 && lines[sharedTabIndex] ? lines[sharedTabIndex].name || `Curve ${sharedTabIndex + 1}` : 'No curve selected'}
                  </Typography>
                </Box>
                <TransformComponent wrapperStyle={{ width: '100%', height: 'auto' }} contentStyle={{ width: '100%', height: 'auto' }}>
                  <div style={{ position: 'relative', display: 'block', width: '100%' }}>
                    <img ref={imgRef} src={graph && graph.crop_url} alt="Crop" onLoad={handleImageLoad} style={{ width: '100%', display: 'block' }} />
                    <svg 
                      ref={svgRef}
                      width={dimensions.width || '100%'} 
                      height={dimensions.height || '100%'} 
                      viewBox={`0 0 ${Math.max(dimensions.naturalWidth, 1)} ${Math.max(dimensions.naturalHeight, 1)}`} 
                      style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%', 
                        pointerEvents: (isEditingMode || isEraseMode) ? 'auto' : 'none',
                        cursor: isEraseMode ? 'cell' : (isEditingMode ? 'crosshair' : 'default')
                      }}
                      onMouseDown={handleSvgMouseDown}
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {lines.map((line) => {
                        if (!line.visible) return null;
                        const segments = getFilteredPolylines(line.img_coords);
                        return segments.map((segment, idx) => (
                          <polyline 
                            key={`${line.id}-segment-${idx}`} 
                            points={pointsToString(segment)} 
                            fill="none" 
                            stroke={line.color || '#ff0000'} 
                            strokeWidth={2} 
                          />
                        ));
                      })}
                    </svg>
                  </div>
                </TransformComponent>
              </>
            )}
          </TransformWrapper>
        </Box>
        <Box
          onMouseDown={() => setIsResizing(true)}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '8px',
            height: '100%',
            cursor: 'ew-resize',
            bgcolor: 'rgba(0,0,0,0.1)',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' },
            zIndex: 10,
          }}
        />
      </Box>
      <Paper ref={rightTabsRef} sx={{ p: 2, ml: { xs: '100%', sm: '5px' }, flexShrink: 0, bgcolor: 'transparent', color: 'inherit', position: rightTabsSticky ? 'sticky' : 'relative', top: rightTabsSticky ? '64px' : 'auto', zIndex: rightTabsSticky ? 999 : 'auto' }}>
          <Box sx={{ width: { xs: '100%', sm: 420 }, flexShrink: 0, alignSelf: 'flex-start', ml: -10 }}>
          <Paper sx={{ p: 1, height: '650px', overflow: 'hidden', bgcolor: '#fff', color: 'text.primary', boxSizing: 'border-box' }} elevation={rightTabsSticky ? 3 : 1}>
            <RightTabs lines={lines} changeName={changeName} toggleLine={toggleLine} changeColor={changeColor} containerHeight={dimensions.height} tabIndex={sharedTabIndex} onTabChange={setSharedTabIndex} scrollRef={rightScrollRef} onScroll={syncRightScroll} draggedPointIndex={draggedPointIndex} isEditingMode={isEditingMode} isEraseMode={isEraseMode} draggedSvgY={draggedSvgY} nullValue={nullValue} />
          </Paper>          
        </Box>
      </Paper>
      <Paper ref={farRightTabsRef} sx={{ p: 2, ml: { xs: '100%', sm: '5px' }, flexShrink: 0, bgcolor: 'transparent', color: 'inherit', position: farRightTabsSticky ? 'sticky' : 'relative', top: farRightTabsSticky ? '64px' : 'auto', zIndex: farRightTabsSticky ? 999 : 'auto' }}>
          <Box sx={{ width: { xs: '100%', sm: 420 }, flexShrink: 0, alignSelf: 'flex-start', ml: 1 }}>
          <Paper sx={{ p: 1, height: '650px', overflow: 'hidden', bgcolor: '#fff', color: 'text.primary', boxSizing: 'border-box' }} elevation={farRightTabsSticky ? 3 : 1}>
            <FarRightTabs lines={lines} changeName={changeName} toggleLine={toggleLine} changeColor={changeColor} containerHeight={dimensions.height} tabIndex={sharedTabIndex} onTabChange={setSharedTabIndex} scrollRef={farRightScrollRef} onScroll={syncFarRightScroll} draggedPointIndex={draggedPointIndex} isEditingMode={isEditingMode} isEraseMode={isEraseMode} draggedSvgY={draggedSvgY} nullValue={nullValue} />
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}

function LeftTabs({ lines, toggleLine, changeColor, changeName, changeUnit, changeAxisMin, changeAxisMax, containerHeight, tabIndex, onTabChange }) {
  const theme = useTheme();
  const heightPx = containerHeight && Number.isFinite(containerHeight) ? `${containerHeight}px` : undefined;

  const allLines = Array.isArray(lines) ? lines : [];
  const safeIndex = allLines.length === 0 ? 0 : Math.max(0, Math.min(tabIndex, allLines.length - 1));

  const [editValue, setEditValue] = useState('');
  const [isEditingHeader, setIsEditingHeader] = useState(false);
  const [tempName, setTempName] = useState('');
  const [tempUnit, setTempUnit] = useState('');
  const [tempAxisMin, setTempAxisMin] = useState('');
  const [tempAxisMax, setTempAxisMax] = useState('');

  useEffect(() => {
    const current = allLines[safeIndex];
    setEditValue(current && current.name ? current.name : '');
    // Auto-toggle off when tab changes
    setIsEditingHeader(false);
  }, [safeIndex, allLines]);

  if (allLines.length === 0) return null;

  // helper: try to extract a unit from a combined name string
  const extractUnit = (name) => {
    if (!name || typeof name !== 'string') return '';
    // parenthetical unit: "Gamma (gAPI)"
    const paren = name.match(/\(([^)]+)\)\s*$/);
    if (paren && paren[1]) return paren[1];
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return '';
    const last = parts[parts.length - 1];
    // heuristic: unit-like token (letters, digits, %, °, Ω, μ, ·, /, ^, +, -)
    if (/^[A-Za-z0-9%°Ωμ·/\\^+\-]+$/.test(last)) return last;
    return '';
  };

  const current = allLines[safeIndex] || {};
  const units = current.unit || extractUnit(current.name || '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: heightPx || '100%', minHeight: 0, maxHeight: '80vh' }}>
      <Tabs orientation="horizontal" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={safeIndex} onChange={(_, v) => onTabChange(v)} sx={{ borderBottom: 1, borderColor: 'divider', overflow: 'auto', bgcolor: '#fff' }} aria-label="curve tabs">
        {allLines.map((line, i) => (
          <Tab key={`${line && line.id ? line.id : 'line'}-${i}`} label={`${line && line.name ? line.name : `Curve ${i + 1}`}${line && line.visible === false ? ' (hidden)' : ''}`} wrapped sx={{ textTransform: 'none', color: 'text.primary', alignItems: 'flex-start' }} />
        ))}
      </Tabs>

      <Box sx={{ p: 2, flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}>
          <Typography variant="body2" sx={{ width: 120, color: 'text.secondary'}}>Curve Visible</Typography>
          <Checkbox checked={!!current.visible} onChange={() => toggleLine(current.id)} sx={{ color: '#fff', '&.Mui-checked': { color: theme.palette.primary.main } }} inputProps={{ 'aria-label': `${current.name || 'curve'} visible` }} />
          <Typography variant="body2" sx={{ width: 140, color: 'text.secondary'}}>
            Curve Color{' '}
            <Typography component="span" variant="caption" sx={{ ml: 0.5, verticalAlign: 'middle', color: 'text.secondary' }}>
              Click to Change
            </Typography>
          </Typography>
          <TextField type="color" value={current.color || '#ff0000'} onChange={(e) => changeColor(current.id, e.target.value)} size="small" sx={{ width: 56, ml: 1, flexShrink: 0 }} inputProps={{ 'aria-label': `${current.name || 'curve'} color` }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ width: 100, color: 'text.secondary', mr: 1 }}>Curve Name</Typography>
          <TextField 
            size="small" 
            value={isEditingHeader ? tempName : current.name || ''} 
            onChange={(e) => isEditingHeader && setTempName(e.target.value)}
            InputProps={{ readOnly: !isEditingHeader }} 
            sx={{ flexGrow: 1 }} 
            inputProps={{ 'aria-label': 'curve name' }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ width: 100, color: 'text.secondary', mr: 1 }}>Units</Typography>
          <TextField 
            size="small" 
            value={isEditingHeader ? tempUnit : units || ''} 
            onChange={(e) => isEditingHeader && setTempUnit(e.target.value)}
            InputProps={{ readOnly: !isEditingHeader }} 
            sx={{ flexGrow: 1 }} 
            inputProps={{ 'aria-label': 'curve units' }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ width: 100, color: 'text.secondary', mr: 1 }}>Axis Min</Typography>
          <TextField 
            size="small" 
            value={isEditingHeader ? tempAxisMin : current.axis_min || ''} 
            onChange={(e) => isEditingHeader && setTempAxisMin(e.target.value)}
            InputProps={{ readOnly: !isEditingHeader }} 
            sx={{ flexGrow: 1 }} 
            inputProps={{ 'aria-label': 'curve axis min' }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Typography variant="body2" sx={{ width: 100, color: 'text.secondary', mr: 1 }}>Axis Max</Typography>
          <TextField 
            size="small" 
            value={isEditingHeader ? tempAxisMax : current.axis_max || ''} 
            onChange={(e) => isEditingHeader && setTempAxisMax(e.target.value)}
            InputProps={{ readOnly: !isEditingHeader }} 
            sx={{ flexGrow: 1 }} 
            inputProps={{ 'aria-label': 'curve axis max' }} 
          />
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              if (isEditingHeader) {
                // Save changes
                changeName(current.id, tempName);
                changeUnit(current.id, tempUnit);
                changeAxisMin(current.id, tempAxisMin);
                changeAxisMax(current.id, tempAxisMax);
              } else {
                // Enter edit mode
                setTempName(current.name || '');
                setTempUnit(units || '');
                setTempAxisMin(current.axis_min || '');
                setTempAxisMax(current.axis_max || '');
              }
              setIsEditingHeader(!isEditingHeader);
            }}
            sx={{
              backgroundColor: isEditingHeader ? '#3ba1a8ff' : '#000000ff',
              '&:hover': { backgroundColor: isEditingHeader ? '#3ba1a8ff' : '#5a5a5aff' }
            }}
          >
            {isEditingHeader ? 'Save Changes' : 'Edit Header Data'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

function RightTabs({ lines, containerHeight, tabIndex, onTabChange, scrollRef, onScroll, draggedPointIndex, isEditingMode, isEraseMode, draggedSvgY, nullValue }) {
  const theme = useTheme();

  const allLines = Array.isArray(lines) ? lines : [];
  const safeIndex = allLines.length === 0 ? 0 : Math.max(0, Math.min(tabIndex, allLines.length - 1));

  const [editValue, setEditValue] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const rowHeight = 33;
  const maxVisibleRows = 15;
  
  useEffect(() => {
    const current = allLines[safeIndex];
    setEditValue(current && current.name ? current.name : '');
  }, [safeIndex, allLines]);

  // Auto-scroll when editing a point - use SVG Y coordinate to find the point index
  useEffect(() => {
    if (draggedPointIndex !== null && (isEditingMode || isEraseMode) && draggedSvgY !== null) {
      const current = allLines[safeIndex];
      if (current && Array.isArray(current.plot_points)) {
        // Find which point is closest to the dragged Y position
        let closestIdx = 0;
        let closestDist = Infinity;
        current.plot_points.forEach((pt, idx) => {
          const ptY = Array.isArray(pt) ? pt[1] : (pt && pt.y);
          if (ptY !== undefined) {
            const dist = Math.abs(ptY - draggedSvgY);
            if (dist < closestDist) {
              closestDist = dist;
              closestIdx = idx;
            }
          }
        });
        
        const scrollPosition = Math.max(0, closestIdx * rowHeight - 50);
        // Update state for virtual rendering
        setScrollTop(scrollPosition);
        // Also update DOM scroll position
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollPosition;
        }
      }
    }
  }, [draggedSvgY, draggedPointIndex, isEditingMode, isEraseMode, rowHeight, scrollRef, allLines, safeIndex]);

  if (allLines.length === 0) return null;

  // helper: try to extract a unit from a combined name string
  const extractUnit = (name) => {
    if (!name || typeof name !== 'string') return '';
    // parenthetical unit: "Gamma (gAPI)"
    const paren = name.match(/\(([^)]+)\)\s*$/);
    if (paren && paren[1]) return paren[1];
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return '';
    const last = parts[parts.length - 1];
  };

  const current = allLines[safeIndex] || {};

  const heightPx = containerHeight && Number.isFinite(containerHeight) ? `${containerHeight}px` : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: heightPx || '100%', minHeight: 0 }}>
      <Tabs orientation="horizontal" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={safeIndex} onChange={(_, v) => onTabChange(v)} sx={{ borderBottom: 1, borderColor: 'divider', overflow: 'auto', bgcolor: '#fff' }} aria-label="curve tabs">
        {allLines.map((line, i) => (
          <Tab key={`${line && line.id ? line.id : 'line'}-${i}`} label={`${line && line.name ? line.name : `Curve ${i + 1}`}${line && line.visible === false ? ' (hidden)' : ''}`} wrapped sx={{ textTransform: 'none', color: 'text.primary', alignItems: 'flex-start' }} />
        ))}
      </Tabs>

      <Box sx={{ p: 2, flexGrow: 1}}>
        <Box >
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Data Points</Typography>
          {Array.isArray(current.plot_points) && current.plot_points.length > 0 ? (
            (() => {
              const raw = Array.isArray(current.plot_points) ? current.plot_points : [];              
              const maxHeight = 530;
              const startIdx = Math.max(0, Math.floor(scrollTop / rowHeight));
              const endIdx = Math.min(raw.length, startIdx + maxVisibleRows);
              const visibleRows = raw.slice(startIdx, endIdx);
              const offsetY = startIdx * rowHeight;
              const totalHeight = raw.length * rowHeight;

              return (
                <>
                  <TableContainer 
                    ref={scrollRef}
                    onScroll={(e) => {
                      setScrollTop(e.target.scrollTop);
                      onScroll(e.target.scrollTop);
                    }}
                    sx={{ maxHeight: `${maxHeight}px`, border: '1px solid rgba(0,0,0,0.08)', overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.8rem' }}>Depth</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={2} sx={{ p: 0, border: 0, height: `${totalHeight}px`, position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: `${offsetY}px`, width: '100%' }}>
                              <Table size="small">
                                <TableBody>
                                  {visibleRows.map((pt, idx) => (
                                    <TableRow key={startIdx + idx} sx={{ height: `${rowHeight}px` }}>
                                      <TableCell sx={{ fontSize: '0.8rem', width: '50%' }}>{Array.isArray(pt) ? pt[1] : (pt && pt.y) ?? ''}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem', width: '50%' }}>{Array.isArray(pt) ? pt[0] : (pt && pt.x) ?? ''}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data points available.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function FarRightTabs({ lines, containerHeight, tabIndex, onTabChange, scrollRef, onScroll, draggedPointIndex, isEditingMode, isEraseMode, draggedSvgY, nullValue }) {
  const theme = useTheme();

  const allLines = Array.isArray(lines) ? lines : [];
  const safeIndex = allLines.length === 0 ? 0 : Math.max(0, Math.min(tabIndex, allLines.length - 1));

  const [editValue, setEditValue] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const rowHeight = 33;
  const maxVisibleRows = 15;
  
  useEffect(() => {
    const current = allLines[safeIndex];
    setEditValue(current && current.name ? current.name : '');
  }, [safeIndex, allLines]);

  // Auto-scroll when editing a point - use SVG Y coordinate to find the point index
  useEffect(() => {
    if (draggedPointIndex !== null && (isEditingMode || isEraseMode) && draggedSvgY !== null) {
      const current = allLines[safeIndex];
      if (current && Array.isArray(current.img_coords)) {
        // Find which point is closest to the dragged Y position
        let closestIdx = 0;
        let closestDist = Infinity;
        current.img_coords.forEach((pt, idx) => {
          const ptY = Array.isArray(pt) ? pt[1] : (pt && pt.y);
          if (ptY !== undefined) {
            const dist = Math.abs(ptY - draggedSvgY);
            if (dist < closestDist) {
              closestDist = dist;
              closestIdx = idx;
            }
          }
        });
        
        const scrollPosition = Math.max(0, closestIdx * rowHeight - 50);
        // Update state for virtual rendering
        setScrollTop(scrollPosition);
        // Also update DOM scroll position
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollPosition;
        }
      }
    }
  }, [draggedSvgY, draggedPointIndex, isEditingMode, isEraseMode, rowHeight, scrollRef, allLines, safeIndex]);

  if (allLines.length === 0) return null;

  // helper: try to extract a unit from a combined name string
  const extractUnit = (name) => {
    if (!name || typeof name !== 'string') return '';
    // parenthetical unit: "Gamma (gAPI)"
    const paren = name.match(/\(([^)]+)\)\s*$/);
    if (paren && paren[1]) return paren[1];
    const parts = name.trim().split(/\s+/);
    if (parts.length < 2) return '';
    const last = parts[parts.length - 1];
  };

  const current = allLines[safeIndex] || {};

  const heightPx = containerHeight && Number.isFinite(containerHeight) ? `${containerHeight}px` : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: heightPx || '100%', minHeight: 0 }}>
      <Tabs orientation="horizontal" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile value={safeIndex} onChange={(_, v) => onTabChange(v)} sx={{ borderBottom: 1, borderColor: 'divider', overflow: 'auto', bgcolor: '#fff' }} aria-label="curve tabs">
        {allLines.map((line, i) => (
          <Tab key={`${line && line.id ? line.id : 'line'}-${i}`} label={`${line && line.name ? line.name : `Curve ${i + 1}`}${line && line.visible === false ? ' (hidden)' : ''}`} wrapped sx={{ textTransform: 'none', color: 'text.primary', alignItems: 'flex-start' }} />
        ))}
      </Tabs>

      <Box sx={{ p: 2, flexGrow: 1}}>
        <Box >
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>Data Points</Typography>
          {Array.isArray(current.img_coords) && current.img_coords.length > 0 ? (
            (() => {
              const raw = Array.isArray(current.img_coords) ? current.img_coords : [];
              const maxHeight = 530;
              const startIdx = Math.max(0, Math.floor(scrollTop / rowHeight));
              const endIdx = Math.min(raw.length, startIdx + maxVisibleRows);
              const visibleRows = raw.slice(startIdx, endIdx);
              const offsetY = startIdx * rowHeight;
              const totalHeight = raw.length * rowHeight;

              return (
                <>
                  <TableContainer 
                    ref={scrollRef}
                    onScroll={(e) => {
                      setScrollTop(e.target.scrollTop);
                      onScroll(e.target.scrollTop);
                    }}
                    sx={{ maxHeight: `${maxHeight}px`, border: '1px solid rgba(0,0,0,0.08)', overflowY: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.8rem' }}>Y</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem' }}>X</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell colSpan={2} sx={{ p: 0, border: 0, height: `${totalHeight}px`, position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: `${offsetY}px`, width: '100%' }}>
                              <Table size="small">
                                <TableBody>
                                  {visibleRows.map((pt, idx) => (
                                    <TableRow key={startIdx + idx} sx={{ height: `${rowHeight}px` }}>
                                      <TableCell sx={{ fontSize: '0.8rem', width: '50%' }}>{Array.isArray(pt) ? pt[1] : (pt && pt.y) ?? ''}</TableCell>
                                      <TableCell sx={{ fontSize: '0.8rem', width: '50%' }}>{Array.isArray(pt) ? pt[0] : (pt && pt.x) ?? ''}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              );
            })()
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data points available.</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}